import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Share2, Image as ImageIcon, FileText, Copy, Send, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { toast } from "sonner";
import {
  exportNodeToPng,
  exportNodeToPdf,
  shareOrDownload,
  copyTextToClipboard,
  downloadBlob,
} from "../../lib/shareExport";

/**
 * ShareMenu
 * ----------
 * Unified share-button + dropdown for the four supported actions:
 *   • שתף כתמונה  — PNG via html-to-image, then Web Share or download
 *   • שתף כ-PDF    — A4 PDF via jsPDF (single-page card, or multi-page
 *                   for long lessons when `pdfNode !== imageNode`)
 *   • העתק טקסט    — clipboard
 *   • שיתוף ישיר   — Web Share API (image + text) when available;
 *                   falls back to download.
 *
 * Props
 *   `getImageNode()` → DOM node to rasterise for image / single-page PDF.
 *                     Called lazily so callers can build the off-screen
 *                     card just-in-time.
 *   `getPdfNode()`   → optional DOM node for multi-page PDF; defaults to
 *                     `getImageNode()`. Pass a taller node here for
 *                     lessons.
 *   `getText()`      → plain-text fallback (always required).
 *   `filename`       → base filename without extension.
 *   `title`          → metadata for the native share sheet.
 *
 * Fallback chain (per product spec)
 *   PNG fails → try PDF → copy text → final toast "נסה שוב"
 */
export default function ShareMenu({
  getImageNode,
  getPdfNode,
  getText,
  filename = "torahya",
  title,
  testIdSuffix = "",
  variant = "ghost",
  size = "sm",
  buttonClassName = "",
  iconOnly = false,
  label,
  pdfMultiPage = false,
  align = "end",
  imageSize, // future override
}) {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "en" ? "ltr" : "rtl";
  const resolvedTitle = title || t("share_menu.default_title");
  const resolvedLabel = label || t("share_menu.default_label");
  const [busy, setBusy] = useState(null); // 'image' | 'pdf' | 'native' | null
  const lastBusyRef = useRef(null);

  // Cancel any pending state on unmount so React doesn't warn after the
  // user closes the menu mid-export.
  useEffect(() => () => { lastBusyRef.current = null; }, []);

  const wrap = useCallback(async (kind, fn) => {
    setBusy(kind);
    lastBusyRef.current = kind;
    try {
      await fn();
    } finally {
      if (lastBusyRef.current === kind) setBusy(null);
    }
  }, []);

  // Compose the full fallback chain. The whole graceful-degradation
  // story lives here so individual feature pages don't reinvent it.
  const doImage = useCallback(async () => {
    try {
      const node = getImageNode?.();
      if (!node) throw new Error("no image node");
      const { blob } = await exportNodeToPng(node, { pixelRatio: 2 });
      const text = (getText?.() || resolvedTitle) + "\n\ntorahya.com";
      await shareOrDownload(blob, `${filename}.png`, { title: resolvedTitle, text });
      toast.success(t("share_menu.toast_image_ready"));
    } catch (e) {
      console.error("share image failed", e);
      // graceful fallback → PDF → text
      toast.error(t("share_menu.toast_image_failed"));
      try { await doPdf(); }
      catch { fallbackCopy(); }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getImageNode, getText, filename, resolvedTitle, t]);

  const doPdf = useCallback(async () => {
    try {
      const node = (getPdfNode || getImageNode)?.();
      if (!node) throw new Error("no pdf node");
      const { blob } = await exportNodeToPdf(node, { multiPage: pdfMultiPage, filename: `${filename}.pdf` });
      const text = (getText?.() || resolvedTitle) + "\n\ntorahya.com";
      await shareOrDownload(blob, `${filename}.pdf`, { title: resolvedTitle, text });
      toast.success(t("share_menu.toast_pdf_ready"));
    } catch (e) {
      console.error("share pdf failed", e);
      toast.error(t("share_menu.toast_pdf_failed"));
      fallbackCopy();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getImageNode, getPdfNode, getText, filename, resolvedTitle, pdfMultiPage, t]);

  const doCopy = useCallback(async () => {
    try {
      const text = getText?.();
      if (!text) throw new Error("no text");
      await copyTextToClipboard(`${text}\n\ntorahya.com`);
      toast.success(t("share_menu.toast_copied"), { description: t("share_menu.toast_copied_desc") });
    } catch (e) {
      console.error("share copy failed", e);
      toast.error(t("share_menu.toast_copy_failed"));
    }
  }, [getText, t]);

  const doNative = useCallback(async () => {
    // Tries native share with the image file first (best UX on mobile),
    // falls back to plain-text Web Share, then to copy.
    try {
      const node = getImageNode?.();
      const text = (getText?.() || resolvedTitle) + "\n\ntorahya.com";
      if (node && typeof navigator !== "undefined" && navigator.share) {
        const { blob } = await exportNodeToPng(node, { pixelRatio: 2 });
        const file = new File([blob], `${filename}.png`, { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: resolvedTitle, text });
          return;
        }
        await navigator.share({ title: resolvedTitle, text });
        return;
      }
      // No Web Share API → try the image-download path so something useful happens
      if (node) {
        const { blob } = await exportNodeToPng(node, { pixelRatio: 2 });
        downloadBlob(blob, `${filename}.png`);
        toast.success(t("share_menu.toast_image_downloaded"));
        return;
      }
      fallbackCopy();
    } catch (e) {
      if (e?.name === "AbortError") return; // user cancelled
      console.error("share native failed", e);
      toast.error(t("share_menu.toast_share_failed"));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getImageNode, getText, filename, resolvedTitle, t]);

  const fallbackCopy = useCallback(async () => {
    try {
      const text = getText?.();
      if (text) {
        await copyTextToClipboard(`${text}\n\ntorahya.com`);
        toast.success(t("share_menu.toast_copied_as_text"));
      } else {
        toast.error(t("share_menu.toast_try_again"));
      }
    } catch {
      toast.error(t("share_menu.toast_try_again"));
    }
  }, [getText, t]);

  const trigger = (
    <Button
      variant={variant}
      size={size}
      data-testid={`share-menu-trigger${testIdSuffix ? "-" + testIdSuffix : ""}`}
      aria-label={iconOnly ? resolvedLabel : undefined}
      className={`${buttonClassName} text-muted-foreground hover:text-gold`}
      disabled={!!busy}
    >
      {busy ? (
        <Loader2 className={`h-4 w-4 animate-spin ${iconOnly ? "" : "ml-1"}`} strokeWidth={1.5} />
      ) : (
        <Share2 className={`h-4 w-4 ${iconOnly ? "" : "ml-1"}`} strokeWidth={1.5} />
      )}
      {!iconOnly && <span>{resolvedLabel}</span>}
    </Button>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        dir={dir}
        className="min-w-[220px] bg-card border-border/60"
        data-testid={`share-menu-content${testIdSuffix ? "-" + testIdSuffix : ""}`}
      >
        {/* Native share — featured on mobile when supported */}
        <DropdownMenuItem
          onClick={() => wrap("native", doNative)}
          data-testid="share-menu-native"
          className="gap-2 text-foreground hover:text-gold cursor-pointer focus:text-gold"
        >
          <Send className="h-4 w-4 text-gold" strokeWidth={1.5} />
          <span className="font-heading text-sm">{t("share_menu.direct_share")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => wrap("image", doImage)}
          data-testid="share-menu-image"
          className="gap-2 text-foreground hover:text-gold cursor-pointer focus:text-gold"
        >
          <ImageIcon className="h-4 w-4 text-gold" strokeWidth={1.5} />
          <span className="font-heading text-sm">{t("share_menu.share_as_image")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => wrap("pdf", doPdf)}
          data-testid="share-menu-pdf"
          className="gap-2 text-foreground hover:text-gold cursor-pointer focus:text-gold"
        >
          <FileText className="h-4 w-4 text-gold" strokeWidth={1.5} />
          <span className="font-heading text-sm">{t("share_menu.share_as_pdf")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={doCopy}
          data-testid="share-menu-copy"
          className="gap-2 text-foreground hover:text-gold cursor-pointer focus:text-gold"
        >
          <Copy className="h-4 w-4 text-gold" strokeWidth={1.5} />
          <span className="font-heading text-sm">{t("share_menu.copy_text_with_link")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
